import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import React, {
  Children,
  DetailedHTMLProps,
  PropsWithChildren,
  ReactElement,
  isValidElement,
} from 'react'

import { Button, ButtonProps } from '@ui/components/Button'

import { EASE_OUT } from '@shared/animations/consts'
import {
  ButtonWithSide,
  ButtonWithSideProps,
} from '@shared/components/ButtonWithSide/ButtonWithSide'

import styles from './ModalFooter.module.scss'

export interface FooterSharedProps {
  id?: string
}

export interface ModalFooterProps extends PropsWithChildren, FooterSharedProps {
  direction?: 'row' | 'column'
}

const FOOTER_BUTTON_LAYOUT_TRANSITION = { duration: 0.24, ease: EASE_OUT } as const

const FooterTop: React.FC<PropsWithChildren<FooterSharedProps>> = ({ children }) => (
  <div>{children}</div>
)

const FooterBottom: React.FC<PropsWithChildren<FooterSharedProps>> = ({ children }) => (
  <div>{children}</div>
)

export interface FooterButtonBaseProps
  extends FooterSharedProps,
    Pick<
      ButtonProps,
      'loading' | 'onClick' | 'children' | 'disabled' | 'iconLeft' | 'iconRight' | 'textOverflow'
    > {
  link?: DetailedHTMLProps<React.AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>
}

const FooterButtonPrimary: React.FC<FooterButtonBaseProps> = (props) => {
  const buttonProps = { ...props, fullWidth: true }

  if (props.link) {
    return (
      <a {...props.link} data-elType='footerButton' className={styles.buttonItem}>
        <Button.Primary {...buttonProps} size='lg' />
      </a>
    )
  }

  return <Button.Primary {...buttonProps} size='lg' fullWidth />
}

const FooterButtonBrand: React.FC<FooterButtonBaseProps> = (props) => {
  const buttonProps = { ...props, fullWidth: true }

  if (props.link) {
    return (
      <a {...props.link} data-elType='footerButton' className={styles.buttonItem}>
        <Button.Brand {...buttonProps} size='lg' />
      </a>
    )
  }

  return <Button.Brand {...buttonProps} size='lg' fullWidth />
}

const FooterButtonSecondary: React.FC<FooterButtonBaseProps> = (props) => {
  const buttonProps = { ...props, fullWidth: true }

  if (props.link) {
    return (
      <a {...props.link} className={styles.buttonItem}>
        <Button.Secondary {...buttonProps} fullWidth size='lg' />
      </a>
    )
  }

  return <Button.Secondary {...buttonProps} size='lg' fullWidth />
}

const FooterButtonWithSide: React.FC<Pick<ButtonWithSideProps, 'icon' | 'text' | 'children'>> = (
  props
) => <ButtonWithSide {...props} fullWidth />

const FooterWrapper: React.FC<ModalFooterProps> = ({ children, direction }) => {
  const nodes = Children.toArray(children).filter(isValidElement) as ReactElement[]

  let topEl: ReactElement | null = null
  let bottomEl: ReactElement | null = null
  const buttonEls: ReactElement[] = []
  const passthrough: ReactElement[] = []

  for (const el of nodes) {
    const t = el.type
    if (t === FooterTop && !topEl) {
      topEl = el
    } else if (t === FooterBottom && !bottomEl) {
      bottomEl = el
    } else if (
      t === ModalButton.Primary ||
      t === ModalButton.Secondary ||
      (el.props as any)?.['data-elType'] === 'footerButton'
    ) {
      buttonEls.push(el)
    } else {
      passthrough.push(el)
    }
  }

  return (
    <div className={styles.footer}>
      {topEl}
      {buttonEls.length > 0 && (
        <motion.div
          layout
          className={classNames(styles.buttons, {
            [styles.buttonsRow]: direction === 'row',
            [styles.buttonsColumn]: direction === 'column',
          })}
        >
          <AnimatePresence initial={false}>
            {buttonEls.map((buttonEl, index) => (
              <motion.div
                key={String(buttonEl.key ?? `footer-button-${index}`)}
                layout
                className={styles.buttonItem}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{
                  layout: FOOTER_BUTTON_LAYOUT_TRANSITION,
                  opacity: { duration: 0.16, ease: 'easeOut' },
                  scale: { duration: 0.16, ease: 'easeOut' },
                }}
                style={{ willChange: 'transform, opacity' }}
              >
                {buttonEl}
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}
      {bottomEl}
      {passthrough}
    </div>
  )
}

const ModalButton = {
  Primary: FooterButtonPrimary,
  Brand: FooterButtonBrand,
  Secondary: FooterButtonSecondary,
  WithSide: FooterButtonWithSide,
}

type FooterCompound = React.FC<ModalFooterProps> & {
  Top: typeof FooterTop
  Bottom: typeof FooterBottom
  Secondary: typeof FooterButtonSecondary
  Primary: typeof FooterButtonPrimary
  Brand: typeof FooterButtonBrand
  WithSide: typeof FooterButtonWithSide
}

export const ModalFooter = Object.assign(FooterWrapper, {
  Top: FooterTop,
  Bottom: FooterBottom,
  Secondary: ModalButton.Secondary,
  Primary: ModalButton.Primary,
  Brand: ModalButton.Brand,
  WithSide: ModalButton.WithSide,
}) as FooterCompound
